import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Briefcase, 
  ArrowRight,
  Clock,
  CheckCircle,
  FileText,
  Layers,
  TrendingUp
} from "lucide-react";

const HowToPresentExperiencePage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-primary" />
              <span className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hireun
              </span>
            </Link>
            <Link to="/">
              <Button data-testid="get-started-btn">
                Generate My Script
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-accent/50 to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="secondary" className="mb-4">Career Guide</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How to Present Your Experience in an Interview
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Learn the art of walking through your career in a way that's engaging, relevant, and memorable.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview */}
        <Card className="mb-12">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The 5-Minute Experience Walkthrough
            </h2>
            <p className="text-muted-foreground mb-6">
              When an interviewer asks you to walk through your experience, they want to understand your career trajectory and how you've grown. Here's how to structure a compelling narrative.
            </p>
            <div className="flex items-center gap-4 p-4 bg-secondary/50 rounded-lg">
              <Clock className="w-8 h-8 text-primary" />
              <div>
                <p className="font-semibold">Aim for 4-5 minutes</p>
                <p className="text-sm text-muted-foreground">Long enough to be thorough, short enough to stay engaging</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Structure */}
        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          The Narrative Arc
        </h2>
        
        <div className="space-y-6 mb-12">
          {[
            {
              icon: Layers,
              title: "1. Start with Context",
              points: [
                "Brief mention of education if relevant",
                "What drew you to your field",
                "Your first professional role"
              ]
            },
            {
              icon: TrendingUp,
              title: "2. Show Progression",
              points: [
                "Highlight key transitions and promotions",
                "Explain why you made each move",
                "Quantify achievements when possible"
              ]
            },
            {
              icon: FileText,
              title: "3. Deep Dive on Relevance",
              points: [
                "Spend more time on recent/relevant roles",
                "Connect experiences to the job requirements",
                "Share specific projects and outcomes"
              ]
            },
            {
              icon: Briefcase,
              title: "4. Connect to Now",
              points: [
                "Why you're looking for a change",
                "What excites you about this role",
                "How your experience prepares you"
              ]
            }
          ].map(({ icon: Icon, title, points }) => (
            <Card key={title}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-accent rounded-lg">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {title}
                    </h3>
                    <ul className="space-y-2">
                      {points.map((point, i) => (
                        <li key={i} className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Example */}
        <Card className="mb-12 border-primary/20">
          <CardContent className="p-8">
            <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Example Walkthrough Structure
            </h3>
            <div className="space-y-4 text-sm">
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="font-medium text-primary mb-1">Opening (30 sec)</p>
                <p className="text-muted-foreground italic">
                  "I started my career in marketing after graduating with a degree in Communications. I was drawn to the intersection of creativity and data..."
                </p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="font-medium text-primary mb-1">Early Career (1 min)</p>
                <p className="text-muted-foreground italic">
                  "At my first role at StartupX, I managed social media campaigns. Key achievement: grew engagement by 150% in 6 months..."
                </p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="font-medium text-primary mb-1">Growth & Transition (1.5 min)</p>
                <p className="text-muted-foreground italic">
                  "This led me to a Marketing Manager position at TechCorp, where I built and led a team of 5..."
                </p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="font-medium text-primary mb-1">Recent Role (1.5 min)</p>
                <p className="text-muted-foreground italic">
                  "Currently, as Senior Marketing Manager, I oversee a $2M budget and have launched our most successful campaign to date..."
                </p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="font-medium text-primary mb-1">Why Now (30 sec)</p>
                <p className="text-muted-foreground italic">
                  "I'm excited about this role at [Company] because it combines my experience in B2B marketing with my passion for sustainable technology..."
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-primary text-white mb-12">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Get Your Personalized Script
            </h2>
            <p className="opacity-90 mb-6 max-w-lg mx-auto">
              Upload your CV and let our AI create a polished 5-minute experience walkthrough tailored to your actual career history and the job you're targeting.
            </p>
            <Link to="/">
              <Button variant="secondary" size="lg" data-testid="cta-personalized">
                Generate My Experience Script
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Related Content */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Related Resources
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/tell-me-about-yourself-interview" className="block">
              <Card className="card-hover h-full">
                <CardContent className="p-6">
                  <h4 className="font-semibold mb-2">Tell Me About Yourself</h4>
                  <p className="text-sm text-muted-foreground">
                    Master the most common interview opener
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/interview-questions/software-engineer" className="block">
              <Card className="card-hover h-full">
                <CardContent className="p-6">
                  <h4 className="font-semibold mb-2">Interview Questions by Role</h4>
                  <p className="text-sm text-muted-foreground">
                    Prepare for role-specific questions
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Briefcase className="w-5 h-5" />
            <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hireun
            </span>
          </div>
          <p className="text-sm text-gray-400">
            &copy; 2025 Hireun. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HowToPresentExperiencePage;
